import { useCallback } from "react";
import { makeVar, ReactiveVar, useReactiveVar } from "@apollo/client";
import { Maybe, WebAiChatMessageOutput, Ai_Chat_User_Selections } from "graphql/generated/graphql";
import { AiChatOrigin, AiChatType } from "constants/field-values";

export interface aiChatStatusType {
	chatMessages?: Maybe<Array<WebAiChatMessageOutput>>;
	chatId?: string;
	// Message typed but not sent yet. Written back on unmount so the draft survives navigation.
	chatText?: string;
	chatTitle?: string;
	chatTopic?: string;
	// The word stream the last question is still waiting on. It lives here, and not in the chat
	// window, so an answer that outlives the component — the user navigates away while a brief is
	// being written — can be picked up again: the words are rows in the database, so subscribing to
	// the same stream id replays everything said so far and still delivers the closing marker.
	streamId?: string;
	isBrief?: boolean;
	// Cadence the brief was generated for (DAILY/WEEKLY); labels the brief message.
	briefFrequency?: string | null;
	chatOrigin?: AiChatOrigin;
	installed_product_id?: string;
	shop_product_id?: string;
	guide_id?: string;
	selected_properties_ids?: string[];
	selected_products_ids?: string[];
	selected_installed_products_ids?: string[];
	ai_chat_user_selections?: Maybe<Array<Ai_Chat_User_Selections>>;
	historyOpen?: boolean;
}

export const aiChatStatusInitialValue: aiChatStatusType = {
	chatMessages: [],
	chatId: "",
	chatText: "",
	chatTitle: "",
	chatTopic: "",
	streamId: "",
	isBrief: false,
	briefFrequency: null,
	chatOrigin: AiChatOrigin.PRIMARY,
	installed_product_id: "",
	shop_product_id: "",
	guide_id: "",
	historyOpen: false,
	selected_properties_ids: [],
	selected_products_ids: [],
	selected_installed_products_ids: [],
	ai_chat_user_selections: []
};

// One slot per chat type: the client chat survives navigation, so a shared var would surface the client
// conversation inside /intelligence.
export const aiChatStatusVar: ReactiveVar<aiChatStatusType> = makeVar<aiChatStatusType>(aiChatStatusInitialValue);

export const adminAiChatStatusVar: ReactiveVar<aiChatStatusType> = makeVar<aiChatStatusType>(aiChatStatusInitialValue);

export const ollieAiChatStatusVar: ReactiveVar<aiChatStatusType> = makeVar<aiChatStatusType>(aiChatStatusInitialValue);

export const getAiChatStatusVar = (chatType: AiChatType = AiChatType.CLIENT): ReactiveVar<aiChatStatusType> => {
	switch (chatType) {
		case AiChatType.ADMIN:
			return adminAiChatStatusVar;
		case AiChatType.OLLIE:
			return ollieAiChatStatusVar;
		default:
			return aiChatStatusVar;
	}
};

// Whether the floating Ollie panel is showing. It lives next to the conversation rather than in the
// layout that draws it: every /ollie screen mounts its own copy of that layout, so a panel left open
// has to still be open on the screen the reader navigates to.
export const ollieAssistantOpenVar: ReactiveVar<boolean> = makeVar<boolean>(false);

// Session-scoped: Intelligence resets on every navigation, so a per-mount guard would re-send (and re-bill)
// its auto-start message on every visit.
export const adminAutoStartSentVar: ReactiveVar<boolean> = makeVar<boolean>(false);

// The single definition of which chats survive navigation. Derived rather than stored on the chat row, so
// changing this product rule doesn't make every historical ai_chats row wrong.
export const isChatDiscardedOnNavigation = (chatType: AiChatType, chatOrigin: AiChatOrigin): boolean =>
	chatType === AiChatType.ADMIN || chatOrigin === AiChatOrigin.SPECIALTY;

export const clearAiChatStatus = (chatType: AiChatType = AiChatType.CLIENT) => {
	getAiChatStatusVar(chatType)(aiChatStatusInitialValue);
};

// Logout cleanup.
export const clearAllAiChatStatus = () => {
	aiChatStatusVar(aiChatStatusInitialValue);
	adminAiChatStatusVar(aiChatStatusInitialValue);
	ollieAiChatStatusVar(aiChatStatusInitialValue);
	ollieAssistantOpenVar(false);
	adminAutoStartSentVar(false);
};

export const useAiChatStatus = (chatType: AiChatType = AiChatType.CLIENT) => {
	const statusVar = getAiChatStatusVar(chatType);
	const aiChatStatusValue = useReactiveVar(statusVar);

	const updateAiChatStatus = useCallback(
		(data: aiChatStatusType) => {
			// Read through the var, not the rendered value: several updates can be queued from one render.
			statusVar({
				...statusVar(),
				...data
			});
		},
		[statusVar]
	);

	return {
		aiChatStatusValue,
		updateAiChatStatus
	};
};
